var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var __metadata = (this && this.__metadata) || function (k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
};
var UserAccount_1;
import { Shape } from 'lincd/shapes/Shape';
import { Literal } from 'lincd/models';
import { getPackageShape, linkedShape } from '../package.js';
import { literalProperty, objectProperty } from 'lincd/shapes/SHACL';
import { sioc } from '../ontologies/sioc.js';
import { Person as SchemaPerson } from 'lincd-schema/shapes/Person';
import { Usergroup } from './Usergroup.js';
import { ShapeSet } from 'lincd/collections/ShapeSet';
import { Role } from './Role.js';
import { dcterms } from 'lincd-dcmi/ontologies/dcterms';
import { Boolean } from 'lincd-xsd/shapes/Boolean';
import { XSDDate } from 'lincd-xsd/shapes/XSDDate';
import { getShapeOrSubShape } from 'lincd/utils/ShapeClass';
import { Resource } from 'lincd-rdfs/shapes';
let UserAccount = UserAccount_1 = class UserAccount extends Resource {
    get functions() {
        return this.getAllAs(sioc.has_function, Role);
    }
    get scopes() {
        return this.getAllAs(sioc.has_scope, getPackageShape('Space'));
    }
    get email() {
        return this.getValue(sioc.email);
    }
    set email(val) {
        this.overwrite(sioc.email, new Literal(val));
    }
    get identifier() {
        const id = this.getValue(sioc.id);
        if (isNaN(+id))
            return id;
        return Number(id);
    }
    set identifier(val) {
        this.overwrite(sioc.id, new Literal(`${val}`));
    }
    get name() {
        return this.getValue(sioc.name);
    }
    set name(val) {
        this.overwrite(sioc.name, new Literal(val));
    }
    get avatar() {
        return this.getValue(sioc.avatar);
    }
    set avatar(val) {
        this.overwrite(sioc.avatar, new Literal(val));
    }
    get accountOf() {
        //NOTE: accountOf is connected to SchemaPerson, even though the sioc ontology defines
        //sioc:account_of to have a rdfs:range of foaf:Person
        //but that's ok because schema:Person is defined with owl:equivalentClass foaf:Person in schema.org
        //if any issues occur with existing data (sioc account + foaf person), we'd need to
        // A) map that foaf:Person to schema:Person
        // B) change the shape of schemaPerson to allow foaf:Person properties as well
        // C) add owl:equivalentProperty statements to state foaf& schema properties are equivalent (more quads)
        //    so that SHACL validation doesn't fail (if it considers the inferred properties)
        return getShapeOrSubShape(this.getOne(sioc.account_of), SchemaPerson);
        // return getShapeOrSubShape(this.getOne(sioc.account_of), [
        //   Person,
        //   SchemaPerson,
        // ]);
    }
    set accountOf(val) {
        this.overwrite(sioc.account_of, val.node);
    }
    get accountOfNode() {
        return this.getOne(sioc.account_of);
    }
    get userGroups() {
        return this.getAllAs(sioc.member_of, Usergroup);
    }
    get created() {
        return this.getOneAs(dcterms.created, XSDDate);
    }
    set created(val) {
        this.overwrite(dcterms.created, val.node);
    }
    get owner() {
        return this.getValue(sioc.has_owner);
    }
    set owner(val) {
        this.overwrite(sioc.has_owner, new Literal(val));
    }
    // static getAccountOf(person: Person) {
    static getAccountOf(person) {
        if (person instanceof Shape) {
            let accountNode = person.getOneInverse(sioc.account_of);
            if (accountNode) {
                return getShapeOrSubShape(accountNode, UserAccount_1);
                // return new this(accountNode);
            }
        }
        else {
            throw Error('TODO: getting account of person QResult is not implemented yet. May need to be async using a query.');
        }
    }
};
UserAccount.targetClass = sioc.UserAccount;
__decorate([
    objectProperty({
        path: sioc.has_function,
        shape: Role,
    }),
    __metadata("design:type", ShapeSet),
    __metadata("design:paramtypes", [])
], UserAccount.prototype, "functions", null);
__decorate([
    objectProperty({
        path: sioc.has_scope,
        shape: getPackageShape('Space'),
    }),
    __metadata("design:type", ShapeSet),
    __metadata("design:paramtypes", [])
], UserAccount.prototype, "scopes", null);
__decorate([
    literalProperty({
        path: sioc.email,
        required: false,
        maxCount: 1,
    }),
    __metadata("design:type", String),
    __metadata("design:paramtypes", [String])
], UserAccount.prototype, "email", null);
__decorate([
    literalProperty({
        path: sioc.id,
        maxCount: 1,
    }),
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [Object])
], UserAccount.prototype, "identifier", null);
__decorate([
    literalProperty({
        path: sioc.name,
        maxCount: 1,
    })
    /**
     * returns the name associated with the user account, a.k.a. the "user name"
     * this does NOT have to match with the real name of the person whose account this is
     */
    ,
    __metadata("design:type", String),
    __metadata("design:paramtypes", [String])
], UserAccount.prototype, "name", null);
__decorate([
    literalProperty({
        path: sioc.avatar,
        maxCount: 1,
    }),
    __metadata("design:type", String),
    __metadata("design:paramtypes", [String])
], UserAccount.prototype, "avatar", null);
__decorate([
    objectProperty({
        path: sioc.account_of,
        shape: SchemaPerson,
        maxCount: 1,
    }),
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [Object])
], UserAccount.prototype, "accountOf", null);
__decorate([
    literalProperty({
        path: dcterms.created,
        datatype: Boolean.targetClass,
    }),
    __metadata("design:type", XSDDate),
    __metadata("design:paramtypes", [XSDDate])
], UserAccount.prototype, "created", null);
__decorate([
    literalProperty({
        path: sioc.has_owner,
    }),
    __metadata("design:type", String),
    __metadata("design:paramtypes", [String])
], UserAccount.prototype, "owner", null);
UserAccount = UserAccount_1 = __decorate([
    linkedShape
], UserAccount);
export { UserAccount };
//# sourceMappingURL=UserAccount.js.map